import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { pricingPlans } from '@/mocks/pricingData';

export default function PricingSection() {
  const { t } = useTranslation();

  return (
    <section id="pricing" className="py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-accent-600">{t('pricing.title')}</span>
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mt-2">{t('pricing.subtitle')}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {pricingPlans.map((plan) => {
            const isHighlighted = plan.highlighted;
            const features = t(plan.featuresKey).split(' · ');

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-6 md:p-8 flex flex-col transition-all duration-300 ${
                  isHighlighted
                    ? 'bg-primary-500 text-background-50 scale-[1.02] md:scale-105'
                    : 'bg-background-100 border border-background-200/70 text-foreground-800'
                }`}
              >
                {isHighlighted && plan.badgeKey && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent-500 text-background-50 text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                    {t(plan.badgeKey)}
                  </div>
                )}

                <div className={`w-12 h-12 flex items-center justify-center rounded-full mb-5 ${
                  isHighlighted ? 'bg-background-50/20' : 'bg-primary-100'
                }`}>
                  <i className={`${plan.icon} text-xl ${isHighlighted ? 'text-background-50' : 'text-primary-600'}`}></i>
                </div>

                <h3 className={`font-heading text-xl md:text-2xl font-semibold ${isHighlighted ? 'text-background-50' : 'text-foreground-950'}`}>
                  {t(plan.key)}
                </h3>

                <div className="mt-3 mb-5">
                  <span className={`font-heading text-3xl md:text-4xl font-bold ${isHighlighted ? 'text-background-50' : 'text-foreground-950'}`}>
                    {t(plan.priceKey)}
                  </span>
                  <span className={`text-sm ml-1 ${isHighlighted ? 'text-background-50/70' : 'text-foreground-500'}`}>
                    {t(plan.periodKey)}
                  </span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isHighlighted ? 'text-background-50' : 'text-accent-500'
                      }`}>
                        <i className="ri-check-line text-xs"></i>
                      </div>
                      <span className={`text-sm ${isHighlighted ? 'text-background-50/90' : 'text-foreground-600'}`}>{feature.trim()}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`inline-flex items-center justify-center gap-2 w-full py-3 rounded-full font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    isHighlighted
                      ? 'bg-background-50 text-primary-600 hover:bg-background-100'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  {t('pricing.cta')}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs md:text-sm text-foreground-500 mt-8">{t('pricing.note')}</p>
      </div>
    </section>
  );
}